import { useState } from 'react';
import { useTranslation } from '@frontend/i18n';
import { TextField, InlineNotification, Text, Accordion } from '@frontend/design-system';
import { useRuleRecurrenceForm } from '../../../hooks/settings/use-rule-recurrence-form';
import { AvailabilityFormRow } from '../availability-form/atoms/availability-form-row';
import { AvailabilityFormSection } from '../availability-form/atoms/availability-form-section';
import { AvailabilityMode, GetAvailabilityRuleFormFieldProps } from '../availability-form/types';
import { BookingRulesForm } from './organisms/booking-rules-form';
import { ProviderForm } from './organisms/provider-form';
import { RuleRecurrenceForm } from './organisms/rule-recurrence-form';
import { ServicesForm } from './organisms/services-form';

type Props = {
  locationId: string | undefined;
  providerList: { value: string; label: string }[];
  operatoryList: { value: string; label: string }[];
  getFieldProps: GetAvailabilityRuleFormFieldProps;
  ruleRecurrenceFormProps: ReturnType<typeof useRuleRecurrenceForm<GetAvailabilityRuleFormFieldProps>>;
  availabilityMode: AvailabilityMode;
  isNonInternalBookingWithoutServices: boolean;
};

export const AvailabilityRuleForm = ({
  locationId,
  providerList,
  operatoryList,
  getFieldProps,
  ruleRecurrenceFormProps,
  availabilityMode,
  isNonInternalBookingWithoutServices,
}: Props) => {
  const { t } = useTranslation('schedule-settings');
  const [openAccordionItem, setOpenAccordionItem] = useState<string | null>('services');

  const handleAccordionChange = (value: string | null) => {
    setOpenAccordionItem(value || 'services');
  };

  return (
    <div>
      <AvailabilityFormSection title={t('Display Name')} hasBottomBorder={false}>
        <AvailabilityFormRow>
          <TextField label={t('')} {...getFieldProps('displayName')} placeholder={t('Name')} />
        </AvailabilityFormRow>
      </AvailabilityFormSection>
      <RuleRecurrenceForm locationId={locationId} ruleRecurrenceFormProps={ruleRecurrenceFormProps} />
      <ProviderForm
        providerList={providerList}
        operatoryList={operatoryList}
        getFieldProps={getFieldProps}
        availabilityMode={availabilityMode}
      />
      <Accordion controlledValue={openAccordionItem} onChange={handleAccordionChange}>
        <BookingRulesForm locationId={locationId} getFieldProps={getFieldProps} />
        <ServicesForm locationId={locationId} getFieldProps={getFieldProps} />
      </Accordion>
      {isNonInternalBookingWithoutServices && (
        <InlineNotification status='warn' size='small' css={{ div: { width: '100%' } }}>
          <div css={{ display: 'flex', flexDirection: 'column' }}>
            <Text size='medium'>{t('“Self Bookable” requires selecting services.')}</Text>
            <Text size='medium'>{t('Change booking rules or services to continue')}</Text>
          </div>
        </InlineNotification>
      )}
    </div>
  );
};
